import React, { useState } from 'react';
import styled from 'styled-components';

import { SxProps } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';

import { inferUnit } from 'ff/debrief/utils';
import { ScoreDetails } from 'ff/debrief/models';
import SegmentScore from 'ff/debrief/components/segments/SegmentScore';
import ScoreRange from './ScoreRange';
import Stack from '@mui/material/Stack';

import { useCurrentDebrief } from 'ff/debrief/hooks';

interface Props {
    scoreDetails: ScoreDetails[];
    sx?: SxProps;
}

const ScoreBreakDown = (props: React.PropsWithChildren<Props>) => {
    const { isScoreModeEnabled } = useCurrentDebrief();
    const [expanded, setExpanded] = useState<string | false>(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    // Format value with unit
    const formatValue = (value: number, unit: string): string => {
        if (Math.abs(value) < 1) {
            return `${value.toFixed(2)}${unit}`;
        } else if (unit === '°') {
            return `${value.toFixed(1)}${unit}`;
        }
        return `${Math.round(value)}${unit}`;
    };

    // Calculate deviation from goal range
    const calculateDeviation = (
        sd: ScoreDetails
    ): { value: number; type: 'above' | 'below' | 'within' } | null => {
        const { actualMin, actualMax, goalMin, goalMax } = sd;

        // Check if entirely above range
        if (goalMax !== null && actualMin > goalMax) {
            return { value: actualMin - goalMax, type: 'above' };
        }

        // Check if entirely below range
        if (goalMin !== null && actualMax < goalMin) {
            return { value: goalMin - actualMax, type: 'below' };
        }

        // Check if partially outside range
        if (goalMax !== null && actualMax > goalMax) {
            return { value: actualMax - goalMax, type: 'above' };
        }

        if (goalMin !== null && actualMin < goalMin) {
            return { value: goalMin - actualMin, type: 'below' };
        }

        return null; // Within range
    };

    const handleChange =
        (panel: string) =>
        (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);

            if (isExpanded && scrollContainerRef.current) {
                // Find the accordion element that was clicked
                const accordionElement =
                    event.currentTarget.closest('.MuiAccordion-root');
                if (accordionElement) {
                    // Small delay to allow accordion to expand
                    setTimeout(() => {
                        const containerTop =
                            scrollContainerRef.current!.getBoundingClientRect()
                                .top;
                        const accordionTop =
                            accordionElement.getBoundingClientRect().top;
                        const scrollOffset = accordionTop - containerTop;

                        scrollContainerRef.current!.scrollTo({
                            top:
                                scrollContainerRef.current!.scrollTop +
                                scrollOffset,
                            behavior: 'smooth',
                        });
                    }, 150);
                }
            }
        };

    return (
        <Container sx={props.sx}>
            <HeaderRow spacing={2} container>
                <Grid size={1} />
                <HeaderCell size={5} sx={{ textAlign: 'right' }}>
                    Parameter
                </HeaderCell>
                <HeaderCell size={4} sx={{ textAlign: 'left', px: 4 }}>
                    Goal vs Actual
                </HeaderCell>
                <Grid size={1} />
                <Grid size={1} />
            </HeaderRow>
            <Box
                overflow={'auto'}
                sx={{}}
                flexGrow={1}
                ref={scrollContainerRef}
            >
                {props.scoreDetails?.map(sd => (
                    <Accordion
                        key={sd.id}
                        sx={{
                            bgcolor: expanded === sd.id ? '#DFECF5' : '#F6F8F9',
                            ':hover': { bgcolor: '#DFECF5' },
                            ':hover .downIcon': { visibility: 'visible' },
                            ':hover .rightIcon': { visibility: 'visible' },
                        }}
                        expanded={expanded === sd.id}
                        onChange={handleChange(sd.id)}
                        elevation={0}
                        disableGutters
                        square
                    >
                        <AccordionSummary
                            sx={{
                                '.MuiAccordionSummary-content': {
                                    my: expanded === sd.id ? 1 : 0,
                                },
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                                sx={{ width: '100%' }}
                                alignItems="center"
                            >
                                {expanded !== sd.id ? (
                                    <>
                                        <Grid size={1}>
                                            <ArrowRightRoundedIcon
                                                className="rightIcon"
                                                sx={{
                                                    visibility: 'hidden',
                                                    fontSize: '32px',
                                                }}
                                            />
                                        </Grid>
                                        <RubricCell size={5}>
                                            {sd.label}
                                        </RubricCell>
                                        <VisualizationCell size={5}>
                                            <ScoreRange
                                                goalMin={sd.goalMin}
                                                goalMax={sd.goalMax}
                                                actualMin={sd.actualMin}
                                                actualMax={sd.actualMax}
                                                label={sd.label}
                                                goal={sd.goal}
                                            />
                                        </VisualizationCell>
                                        {isScoreModeEnabled && (
                                            <ScoreCell size={1}>
                                                <SegmentScore
                                                    score={sd.score}
                                                />
                                            </ScoreCell>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Grid size={1}>
                                            <ArrowDropDownRoundedIcon
                                                fontSize="large"
                                                className={'downIcon'}
                                            />
                                        </Grid>
                                        <RubricCell size={5}>
                                            {sd.label}
                                        </RubricCell>
                                        <DeviationCell size={4}>
                                            {(() => {
                                                const deviation =
                                                    calculateDeviation(sd);
                                                if (!deviation) return null;

                                                const unit = inferUnit(
                                                    sd.label,
                                                    sd.goal
                                                );
                                                const deviationText =
                                                    formatValue(
                                                        deviation.value,
                                                        unit
                                                    );
                                                const prefix =
                                                    deviation.type === 'above'
                                                        ? '+'
                                                        : '-';

                                                return (
                                                    <DeviationText
                                                        sx={{
                                                            color: '#FF9800',
                                                        }}
                                                    >
                                                        {prefix}
                                                        {deviationText} out of
                                                        range
                                                    </DeviationText>
                                                );
                                            })()}
                                        </DeviationCell>
                                        {isScoreModeEnabled && (
                                            <WeightCell size={1}>
                                                <Stack>
                                                    <Typography fontSize={10}>
                                                        Weight
                                                    </Typography>
                                                    <Typography
                                                        fontSize={12}
                                                        fontWeight={700}
                                                    >
                                                        {Math.round(
                                                            sd.weight * 100
                                                        )}
                                                        %
                                                    </Typography>
                                                </Stack>
                                            </WeightCell>
                                        )}
                                        {isScoreModeEnabled && (
                                            <ScoreCell size={1}>
                                                <SegmentScore
                                                    score={sd.score}
                                                />
                                            </ScoreCell>
                                        )}
                                    </>
                                )}
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid alignItems={'center'} container>
                                <Grid size={12}>
                                    <ScoreRange
                                        size={'lg'}
                                        goalMin={sd.goalMin}
                                        goalMax={sd.goalMax}
                                        actualMin={sd.actualMin}
                                        actualMax={sd.actualMax}
                                        label={sd.label}
                                        goal={sd.goal}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Container>
    );
};

const Container = styled(Box)`
    padding-bottom: 8px;
    padding-left: 8px;
    padding-right: 8px;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

const HeaderRow = styled(Grid)`
    padding: 8px 16px;
    background-color: #e0e0e0;
`;

const HeaderCell = styled(Grid)`
    color: #666;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const RubricCell = styled(Grid)`
    text-align: end;
    color: #222;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
`;

const VisualizationCell = styled(Grid)``;

const DeviationCell = styled(Grid)``;

const WeightCell = styled(Grid)``;

const ScoreCell = styled(Grid)``;

const DeviationText = styled(Typography)`
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    text-align: right;
`;

export default ScoreBreakDown;
